// node modules
import React, { useEffect, useState, useRef, useMemo } from 'react'
import styled from 'styled-components'
import { Link, useHistory, useParams, useLocation } from 'react-router-dom'
import Linkify from 'react-linkify'
import PropTypes from 'prop-types'
import { isBrowser } from 'react-device-detect'
import 'react-image-lightbox/style.css'
import { useDispatch, useSelector } from 'react-redux'
import { Waypoint } from 'react-waypoint'

// Material-UI
import { makeStyles } from '@material-ui/core/styles'
import { Menu, MenuItem, SwipeableDrawer } from '@material-ui/core'
import {
  MoreHoriz,
  FavoriteBorder,
  DeleteOutline,
  Cancel
} from '@material-ui/icons'
import { Dialog, IconButton } from '@material-ui/core'

// utils
import { limitedPlanPriceSupplement } from 'utils/subscriptionPlan'
import { useQueryStringsHandover } from 'hooks/useQueryStringsHandover'

// components
import LoadingAnimation from 'components/common/LoadingAnimation'
import NotificationPermissionDialog from 'components/common/NotificationPermissionDialog'
import ShareButtonView from 'components/common/ShareButtonView'

// API
import { getDoor, getReviews } from 'apis/doors'
import { deletePost } from 'apis/posts'
import { enableAllPushNotifications } from 'apis/me'
import {
  DoorTop,
  DoorDetail,
  User,
  SamplePost,
  SubscriptionPlanV1,
  Review
} from 'apis/response_types'

// resources
import DummyHeader from '../../images/dummy_header.jpg'
import DefaultUserIcon from '../../images/default_user_icon.jpg'

// resources redesin
import IconShare from '../../images/redesign/icon_share.svg'
import IconShareWhite from '../../images/redesign/icon_share_white.svg'
import IconArrow from '../../images/redesign/icon_arrow.svg'
import IconUser from '../../images/redesign/icon_user.svg'
import IconCalendar from '../../images/redesign/icon_calendar.svg'
import IconCheckmark from '../../images/redesign/icon_checkmark.svg'
import BtnArrowLeftGray from '../../images/redesign/btn_arrow_left_gray.svg'
import BtnArrowLeft from '../../images/redesign/btn_arrow_left.svg'
import BtnArrowRightGray from '../../images/redesign/btn_arrow_right_gray.svg'
import BtnArrowRight from 'images/redesign/btn_arrow_right.svg'
import SecretPlanHeader from 'images/secret_plan_header.svg'
import BannerSalonBeginner from '../../images/banner_salon_membership_method.webp'
import LockIcon from '@material-ui/icons/Lock'
import ScratchBannerDummy from 'images/scratch/sample_header.png'

// sampleIamge
import ImageNormal from '../../images/redesign/image_normal.jpg'

// utils
import dateUtils from '../../utils/Datetime'

// redux
import alertModules from 'modules/alert'
import doorModules from 'modules/door'
import topBannerModules, { TopBannerContent } from 'modules/top_banner'
import BreadCrumbs, { BreadCrumb } from 'components/common/BreadCrumbs'
import samplePostModules from 'modules/sample_posts'

// constants
import { DOOR_CONST } from '../../constants'
import {
  getInvitation,
  Invitation,
  removeInvitationToken,
  getInvitationExecuted,
  removeInvitationExecuted,
  removeInvalidInvitation,
  getInvalidInvitation,
  removeInvitationAlreadyAttended,
  getInvitationAlreadyAttended
} from 'apis/invitation'
import InvitationNotificationModal from 'components/signup/InvitationNotificationModal'
import { SamplePostFilesPreview } from './room/FilePreviews'
import { ImagesPreview } from './room/ImagePreview'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import MetaTag from 'components/common/MetaTag'
import SimpleModal from 'components/common/SimpleModal'
import { SamplePostEditModal } from './SamplePostEditModal'
import classNames from 'classnames'
import PrivacyPolicyModal from 'components/common/PrivacyPolicyModal'
import { ContentAccessDialog } from 'components/door/ContentAccessDialog'
import { RootState } from '../../store'
import { getIsSignin } from '../../modules/me'
dayjs.locale(ja)

function TabPanel(props: any) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value != index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value == index && <>{children}</>}
    </div>
  )
}

export const formatWithWeekDay = 'YYYY年M月D日（dd）'
export const formatDay = 'YYYY年M月D日'
export const formatMonth = 'YYYY年M月'
export const formatWeekDay = '（dd）'
const validateFormatWithoutTime = 'YYYY/M/D'
export const endDate = (plan: SubscriptionPlanV1) => {
  return startDate(plan).add(Number(plan?.joinable_months), 'month')
}
export const startDate = (plan: SubscriptionPlanV1) => {
  return dayjs(plan?.joinable_starts_at, validateFormatWithoutTime)
}

export const perMonth = (month: number) => {
  if (month === 12) {
    return '1年'
  } else {
    return `${month}ヶ月`
  }
}

type LocationState = {
  showContentAccessDialog: boolean
  roomId: number
  callBackUrl: string
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
  className: PropTypes.node
}

function Index(props: any) {
  const { search } = useLocation()
  const location = useLocation<LocationState>()
  const searchParams = new URLSearchParams(search)
  const planSecret = searchParams.get('secret') || undefined
  const pushHistoryWithSecret = useQueryStringsHandover(['secret'])

  const omittHeight = 240
  const [planOmitted, setPlanOmitted] = useState<boolean[]>([])
  const planRefs = useRef<(HTMLDivElement | null)[]>([])
  const [plans, setPlans] = useState<SubscriptionPlanV1[] | undefined>()
  const history = useHistory()
  const dispatch = useDispatch()
  const [togglePlanDescriptions, setTogglePlanDescriptions] = useState<
    boolean[]
  >([])
  const [tabFocused, setTabFocused] = useState<
    'intro' | 'ownerInfo' | 'samples' | 'reviews'
  >('intro')
  const [deleting, setDeleting] = useState(false)
  const [deniedInvitation, setDeniedInvitation] = useState<Invitation | null>(
    null
  )
  const [doorID, setDoorID] = useState(props.doorID)
  const [response, setResponse] = useState<DoorTop | null>(null)
  const [postIdForDelete, setPostIdForDelete] = useState<null | string>(null)
  const [isRelations, setIsRelations] = useState<string | boolean | null>(null)
  const [isOpenSalonSnsShare, setIsOpenSalonSnsShare] = useState<boolean>(false)
  const [memberPath, setMemberPath] = useState('')
  const [planPath, setPlanPath] = useState('')
  const [loading, setLoading] = useState(true)
  const ls = useLocation<{ showNotificationDialog: false }>().state
  const [urlSignatureErrorDialogOpening, setUrlSignatureErrorDialogOpening] =
    useState(false)
  const [showNotificationDialog, setShowNotificationDialog] = useState<boolean>(
    ls !== undefined &&
      ls.showNotificationDialog &&
      isBrowser &&
      Notification.permission === 'default' &&
      localStorage.getItem('dont_show_again') === null
  )
  const breadcrumbsList: BreadCrumb[] = [{ name: response?.door?.title || '' }]
  const reviewPer = 20
  const [reviews, setReviews] = useState<Review[]>()
  const [additionalLoadable, setAdditionalLoadable] = useState(false)
  const [additionalReviewsLoading, setAdditionalReviewsLoading] =
    useState(false)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [contentAccessDialogOpening, setContentAccessDialogOpening] = useState<boolean>(location.state?.showContentAccessDialog)
  const isSignIn = useSelector((state: RootState) => getIsSignin(state))

  const scrollToSalonTab = () => {
    if (salonTab.current) {
      const salonTabCurrent = salonTab.current
      const offset = window.matchMedia('(max-width: 768px)').matches ? 40 : 64

      const salonTabCurrentRect = salonTabCurrent.getBoundingClientRect()
      const absoluteElementTop = salonTabCurrentRect.top + window.scrollY
      const scrollTo = absoluteElementTop - offset

      setTimeout(() => {
        window.scrollTo({
          top: scrollTo,
          behavior: 'smooth'
        })
      }, 0)
    }
  }

  // 納品データをReact用に調整
  const [data, setData] = useState({
    isSp: false,
    isOpenSearch: false,
    isOpenSpMenu: false,
    isHeaderVisible: false,
    isFixedSalonTabList: false,
    isBottomSalonTabList: false,
    currentSalonTabIndex: 1
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      if (plans) {
        const newplanOmitted = plans.map(
          (_, index) => planRefs.current[index]?.offsetHeight! > omittHeight
        )
        setPlanOmitted(newplanOmitted)
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [plans])

  const salonTab = useRef<HTMLDivElement>(null)
  const salonTabContent = useRef<HTMLDivElement>(null)

  const handleClickSalonSnsShare = () => {
    setIsOpenSalonSnsShare(!isOpenSalonSnsShare)
  }

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setData((prevData) => ({ ...prevData, isSp: true }))
    }
  }, [])

  // コンポーネントが破棄される際にカスタム表示のフラグを解除する。
  // これをしないとドアページ以外でもカスタム表示の影響が出てしまうため。
  useEffect(() => {
    initInvitation()
    return () => {
      dispatch(
        doorModules.actions.setCustomize({
          customize: false
        })
      )
    }
  }, [])

  const omittString = (str: string | null, num: number) => {
    if (str === null) return
    return str.length > num ? `${str.slice(0, num)}...` : str
  }

  const freeTrialText = (freeTrialPeriod: string) => {
    let text = ''
    if (freeTrialPeriod === '-1') {
      text = '初月無料'
    } else {
      text = `${freeTrialPeriod}日間無料`
    }
    return text
  }

  const initInvitation = () => {
    if (getInvitationExecuted()) {
      removeInvitationExecuted()
      dispatch(
        alertModules.actions.setAlert({
          text: '参加が完了しました',
          severity: 'success'
        })
      )
    }
    if (getInvitationAlreadyAttended()) {
      removeInvitationAlreadyAttended()
      removeInvitationToken()
      dispatch(
        alertModules.actions.setAlert({
          text: 'すでに参加しているサロンです',
          severity: 'warning'
        })
      )
    }
    invalidInvitation()
  }

  const invalidInvitation = async () => {
    const invalidToken = getInvalidInvitation()
    if (invalidToken) {
      removeInvalidInvitation()
      const resInvitation = await getInvitation(invalidToken)
      const invitation: Invitation = resInvitation.data
      setDeniedInvitation(invitation)
    }
  }

  const planButtonText = (plan: SubscriptionPlanV1) => {
    let text = ''
    if (plan.is_suspended) {
      text = '募集停止中'
    } else if (plan.is_subscribing) {
      text = 'このプランで参加中'
    } else if (plan.is_capacity_limit) {
      text = '現在定員に達しています'
    } else if (plan.joinable_starts_at !== null) {
      if (dayjs(plan.joinable_starts_at).valueOf() > Date.now()) {
        text = plan.is_reserving ? '申し込み済み' : 'このプランに申し込む'
      } else {
        text = 'このプランに参加'
      }
    } else {
      text = 'このプランに参加'
    }
    return text
  }

  const handleDeletePost = async () => {
    setDeleting(true)
    await deletePost(postIdForDelete!)
      .then(() => {
        setPostIdForDelete(null)
      })
      .catch(() => {
        dispatch(
          alertModules.actions.setAlert({
            text: '削除に失敗しました',
            severity: 'error'
          })
        )
      })
      .finally(() => {
        setDeleting(false)
      })
  }

  const reviewHandleScroll = async () => {
    if (!additionalLoadable || additionalReviewsLoading) return
    setAdditionalReviewsLoading(true)
    const nextPage = currentPage + 1
    const response = await getReviews(doorID, nextPage, reviewPer)
    setCurrentPage(nextPage)
    setReviews(reviews?.concat(response.data.reviews))
    setAdditionalLoadable(response.data.reviews.length === reviewPer)
    setAdditionalReviewsLoading(false)
  }

  const loadReviews = async () => {
    const response = await getReviews(doorID, 1, reviewPer)
    setReviews(response.data.reviews)
    setAdditionalLoadable(response.data.reviews.length === reviewPer)
  }

  const initPage = async (doorID: string) => {
    setLoading(true)

    // ドア情報読み込み前にカスタム表示設定をONにする。
    // yoorのロゴをドア情報が読み込まれるまで非表示にしたい。
    // カスタム表示無効のままだと、ヘッダーにyoorのロゴ等がドアデータの取得が完了されるまで表示されてしまう。
    dispatch(
      doorModules.actions.setCustomize({
        customize: true
      })
    )
    loadReviews()
    await getDoor(doorID, planSecret)
      .then((response) => {
        setResponse(response.data)
        const is_relations = response.data.owned || response.data.staff
        setPlanPath(
          response.data.staff
            ? `/door/${doorID}/management/plans`
            : '/management/plans'
        )
        setMemberPath(
          response.data.staff
            ? `/door/${doorID}/management/door/members`
            : '/management/door/members'
        )
        setTogglePlanDescriptions(
          new Array(response?.data?.door?.subscription_plans.length).fill(false)
        )
        if (response.data.invalid_plan_secret_signature) {
          setUrlSignatureErrorDialogOpening(true)
        }
        setIsRelations(is_relations)
        if (response.data.door) {
          dispatch(
            doorModules.actions.setCustomize({
              customize: response.data.door.customize
            })
          )
          dispatch(
            samplePostModules.actions.setSamplePosts(
              response.data.door.sample_posts
            )
          )
          setPlans(response.data.door.subscription_plans)
          if (!is_relations) {
            return
          }

          let content: TopBannerContent | null = null
          // 非公開状態
          if (response.data.door.status === 'in_private') {
            if (response.data.can_publish) {
              content = 'door_can_publish'
            } else {
              if (response.data.staff && !response.data.owner_profile_filled) {
                content = 'door_owner_personal_info_required'
              } else {
                content = 'door_cannot_publish'
              }
            }
          }
          // 公開状態
          else if (
            (response.data.owned &&
              (!response.data.can_publish ||
                response.data.tax_exemption === null)) ||
            (response.data.staff && !response.data.profile_filled)
          ) {
            // 公開済みだが必須項目が満たされていないdoor。非公開機能実装前のdoorが該当する
            content = 'door_personal_info_required'
          }
          if (content) {
            const targetConfig =
              content === 'door_owner_personal_info_required'
                ? 'personal'
                : 'door'

            let configPath = ''
            if (response.data.owned) {
              // インボイス情報登録項目
              if (
                response.data.profile_filled &&
                response.data.tax_exemption === null
              ) {
                configPath = `/management/personal/edit`
              } else {
                configPath = `/management/${targetConfig}/edit`
              }
            } else {
              configPath = `/door/${doorID}/management/${targetConfig}/edit`
            }

            dispatch(
              topBannerModules.actions.setMessage({
                content: content,
                configPath: configPath
              })
            )
          }
        } else {
          dispatch(
            doorModules.actions.setCustomize({
              customize: false
            })
          )
        }
      })
      .catch((error) => {
        const status = error.response.status
        switch (status) {
          case 404: // ユーザが存在しない
            history.push({
              pathname: '/error',
              state: { type: 'DoorNotFound' }
            })
            break
          // case 423:
          //   history.push({
          //     pathname: '/error',
          //     state: { type: 'Suspended' }
          //   })
          //   break
        }
        //　エラー時は常にカスタム表示無効化
        dispatch(
          doorModules.actions.setCustomize({
            customize: false
          })
        )
      })
    setLoading(false)
  }
  useEffect(() => {
    if (props.doorID !== doorID) {
      dispatch(topBannerModules.actions.setClose())
      setDoorID(props.doorID)
      initPage(props.doorID)
    }
  }, [props.doorID])

  useEffect(() => {
    initPage(props.doorID)

    return () => {
      dispatch(topBannerModules.actions.setClose())
    }
  }, [])

  const grantedCallback = () => {
    enableAllPushNotifications()
  }

  const navigateToUnspecifiedPlanSelectionOrRoom = async () => {
    if (response?.door?.subscribing || isRelations) {
      // オーナーもしくはサブスク済みはルームへ遷移
      history.push(`/room/${response?.owner.id}`)
    } else {
      // プラン一覧へ
      pushHistoryWithSecret(`/door/${response?.owner.id}/plans`)
    }
  }

  return (
    <React.Fragment>
      <PrivacyPolicyModal />
      {loading ? (
        <div style={{ height: '100dvh', backgroundColor: '#FFF' }}>
          &nbsp;
          <LoadingAnimation />
        </div>
      ) : (
        response && (
          <>
            <MetaTag
              title={`${
                response.door?.title || `${response.owner.name}のマイページ`
              } - オンラインサロンYOOR（ユア）`}
              description={response.door?.description}
              ogImage={response.owner.header}
            />

            {(() => {
              if (
                // サロン開設済
                response.door &&
                // オーナーorスタッフ
                (isRelations ||
                  // 閲覧権限あり
                  response.door.force_view ||
                  // サロン公開済
                  response.door.status === 'published')
              ) {
                return (
                  <SalonTop>
                    <SalonTopFixedBox>
                      <div className="salon-top-fixed-box-inner">
                        <button
                          type="button"
                          className={`button button-blue is-text-center modal-open ${
                            (response?.door?.subscribing || isRelations) &&
                            'subscribed'
                          }`}
                          data-modal-target="modal-door"
                          onClick={navigateToUnspecifiedPlanSelectionOrRoom}
                        >
                          <span className="button button-icon-arrow">
                            {response?.door?.subscribing || isRelations
                              ? 'トークルームへ'
                              : 'このオンラインサロンに参加する'}
                          </span>
                        </button>
                        <button
                          className={`salon-top-sns-button ${
                            isOpenSalonSnsShare ? 'is-open' : ''
                          }`}
                          aria-label="Toggle SNS Share Options"
                          aria-expanded={isOpenSalonSnsShare ? 'true' : 'false'}
                          onClick={handleClickSalonSnsShare}
                        ></button>
                        {isOpenSalonSnsShare && (
                          <>
                            {response?.door &&
                              response?.door.status === 'published' && ( // door未開設時はシェアURLを開いてもエラーになってしまうのでシェアボタンは表示させない
                                <section className="share share-side">
                                  <h3 className="share-title">SHARE</h3>
                                  <ShareButtonView
                                    style={{ marginTop: '56px' }}
                                    doorName={response?.door?.title}
                                    ownerName={response?.owner.name}
                                  />
                                </section>
                              )}
                          </>
                        )}
                      </div>
                    </SalonTopFixedBox>

                    <SalonTopTopBg
                      style={{
                        backgroundImage: `url(${
                          response.owner.header
                            ? response.owner.header +
                              '?crop=center&fit=crop&width=700&height=400'
                            : DummyHeader
                        })`
                      }}
                    >
                      <div className="salon-top-filter"></div>
                    </SalonTopTopBg>

                    <SalonTopInner>
                      <BreadCrumbsStyled breadcrumbsList={breadcrumbsList} />
                      <div className="salon-top-content">
                        <div className="salon-top-text-box">
                          <h2 className="salon-top-sub-title">
                            {response?.door?.title || (
                              <>ここにサロン名が表示されます</>
                            )}
                          </h2>
                          <div className="salon-top-user-image-box">
                            <div
                              className="salon-top-user-image"
                              style={{
                                backgroundImage: `url(${
                                  response.owner.icon
                                    ? response.owner.icon
                                    : DefaultUserIcon
                                })`
                              }}
                            ></div>
                            <p className="salon-top-name">
                              {response.owner.name}
                            </p>
                          </div>
                          <p className="salon-top-text">
                            {(() => {
                              if (
                                response?.door &&
                                (isRelations ||
                                  response?.door.force_view ||
                                  response?.door.status === 'published')
                              ) {
                                // サロンが存在していて、かつサロンオーナーorサロンが公開ステータスなら
                                return (
                                  omittString(
                                    response?.door.description,
                                    DOOR_CONST.SHORT_DESCRIPTION_MAX_LENGTH
                                  ) ||
                                  response?.owner.description || (
                                    <>ここにサロン簡易説明が表示されます</>
                                  )
                                )
                              } else {
                                return response?.owner.description
                              }
                            })()}
                          </p>
                          {response?.door?.door_recommends &&
                            response.door.door_recommends.some(
                              (r) => r !== ''
                            ) && (
                              <div className="salon-top-text">
                                <span className="salon-top-title-red">
                                  こんな方におすすめ
                                </span>
                                <ul>
                                  {response?.door?.door_recommends
                                    ?.filter((r) => r !== '')
                                    .map((recommend) => (
                                      <li>●{recommend}</li>
                                    ))}
                                </ul>
                              </div>
                            )}

                          <div className="salon-top-bottom-box">
                            <ul className="salon-top-status-list">
                              {response?.door?.is_display_member_count &&
                                response?.door?.member_count !== null && (
                                  <li className="salon-top-status-item">
                                    <span
                                      className="salon-top-icon"
                                      style={{
                                        backgroundImage: `url(${IconUser})`
                                      }}
                                    ></span>
                                    <span className="salon-top-status-text">
                                      メンバー数
                                    </span>
                                    <span className="salon-top-number">
                                      <span className="salon-top-status-large">
                                        {(() => {
                                          const member_count =
                                            response.door.member_count
                                          if (isRelations) {
                                            // オーナーの時のみメンバー一覧へのリンクとする
                                            return (
                                              <Link
                                                to={memberPath}
                                                rel="nofollow"
                                              >
                                                {member_count}
                                              </Link>
                                            )
                                          } else {
                                            return member_count
                                          }
                                        })()}
                                      </span>
                                      人
                                    </span>
                                  </li>
                                )}
                              <li className="salon-top-status-item">
                                <span
                                  className="salon-top-icon"
                                  style={{
                                    backgroundImage: `url(${IconCalendar})`
                                  }}
                                ></span>
                                <span className="salon-top-status-text">
                                  投稿数
                                </span>
                                <span className="salon-top-number">
                                  <span className="salon-top-status-large">
                                    {response?.door?.post_count || 0}
                                  </span>
                                </span>
                              </li>
                              {response?.door?.latest_post_updated_at && (
                                <p className="salon-top-date">
                                  <span className="salon-top-small-text">
                                    最終投稿
                                  </span>{' '}
                                  {response?.door?.latest_post_updated_at}
                                </p>
                              )}
                            </ul>
                            <div className="salon-top-button-box pc-only">
                              <button
                                type="button"
                                className={`button button-blue is-text-center modal-open ${
                                  (response?.door?.subscribing ||
                                    isRelations) &&
                                  'subscribed'
                                }`}
                                data-modal-target="modal-door"
                                onClick={
                                  navigateToUnspecifiedPlanSelectionOrRoom
                                }
                              >
                                <span className="button button-icon-arrow">
                                  {response?.door?.subscribing || isRelations
                                    ? 'トークルームへ'
                                    : 'このオンラインサロンに参加する'}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="salon-top-main-image-wrap">
                          <div
                            className="salon-top-main-image"
                            style={{
                              backgroundImage: `url(${
                                response.owner.header
                                  ? response.owner.header +
                                    '?crop=center&fit=crop&width=700&height=400'
                                  : DummyHeader
                              })`
                            }}
                          ></div>
                        </div>
                      </div>
                    </SalonTopInner>

                    <SalonTab ref={salonTab}>
                      <a className="salon-tab-anker" rel="nofollow"></a>
                      <div className="salon-tab-inner">
                        <div className="salon-tab-list-wrap">
                          {/* <!--salint-tab-list-inner is-fixed でFIXED--> */}
                          <div className={`salon-tab-list-inner`}>
                            <ul className="salon-tab-list">
                              <li className="salon-tab-item">
                                <a
                                  href="#salon-tab-anker"
                                  rel="nofollow"
                                  onClick={(e) => {
                                    scrollToSalonTab()
                                    e.preventDefault()
                                    setTabFocused('intro')
                                  }}
                                  className={`salon-tab-link ${
                                    tabFocused === 'intro' ? 'is-current' : ''
                                  }`}
                                >
                                  サロン紹介
                                </a>
                              </li>
                              <li className="salon-tab-item">
                                <a
                                  href="#salon-tab-anker"
                                  rel="nofollow"
                                  onClick={(e) => {
                                    scrollToSalonTab()
                                    e.preventDefault()
                                    setTabFocused('ownerInfo')
                                  }}
                                  className={`salon-tab-link ${
                                    tabFocused === 'ownerInfo'
                                      ? 'is-current'
                                      : ''
                                  }`}
                                >
                                  オーナー情報
                                </a>
                              </li>
                              <li className="salon-tab-item">
                                <a
                                  href="#salon-tab-anker"
                                  rel="nofollow"
                                  onClick={(e) => {
                                    scrollToSalonTab()
                                    e.preventDefault()
                                    setTabFocused('samples')
                                  }}
                                  className={`salon-tab-link ${
                                    tabFocused === 'samples' ? 'is-current' : ''
                                  }`}
                                >
                                  投稿サンプル
                                </a>
                              </li>
                              {(isRelations ||
                                (reviews !== undefined &&
                                  reviews.length > 0)) && (
                                <li className="salon-tab-item">
                                  <a
                                    href="#salon-tab-anker"
                                    onClick={(e) => {
                                      scrollToSalonTab()
                                      e.preventDefault()
                                      setTabFocused('reviews')
                                    }}
                                    className={`salon-tab-link ${
                                      tabFocused === 'reviews'
                                        ? 'is-current'
                                        : ''
                                    }`}
                                  >
                                    口コミ
                                  </a>
                                </li>
                              )}
                            </ul>
                            {response?.door &&
                              response?.door.status === 'published' && ( // door未開設時はシェアURLを開いてもエラーになってしまうのでシェアボタンは表示させない
                                <section className="share share-side pc-only">
                                  <h3 className="share-title">SHARE</h3>
                                  <ShareButtonView
                                    doorName={response?.door?.title}
                                    ownerName={response?.owner.name}
                                    style={{ marginTop: '56px' }}
                                  />
                                </section>
                              )}
                          </div>
                        </div>
                        <div
                          className={`salon-tab-content ${
                            data.isFixedSalonTabList ? 'is-fixed-tab-list' : ''
                          }`}
                          ref={salonTabContent}
                        >
                          <div className="salon-tab-main-content">
                            <section className="salon-information">
                              <h3 className="salon-information-title">
                                <span className="salon-information-bold">
                                  SALON
                                </span>{' '}
                                INFORMATION
                              </h3>
                              <ul className="salon-information-box-list">
                                <li
                                  className={`salon-information-box-list-item ${
                                    tabFocused === 'intro' ? 'is-current' : ''
                                  }`}
                                >
                                  <IntroduceTab
                                    doorId={doorID}
                                    door={response.door}
                                    owned={response.owned}
                                    staff={response.staff}
                                  />
                                  <div className="salon-information-banner-box pc-only">
                                    <a
                                      href="https://join-howto.yoor.jp/?_gl=1*1ceelqa*_ga*MTY5NDgwOTI3MC4xNjU5MzQwMDU2*_ga_18HYVMJFLJ*MTY5OTYwMzQ1Ni4xMTA0LjEuMTY5OTYwMzQ2NC41Mi4wLjA."
                                      className="salon-information-banner"
                                      target="_blank"
                                    >
                                      <img
                                        src={BannerSalonBeginner}
                                        className="salon-information-banner-image"
                                        alt="初めての方へ　サロン入会の方法はこちら"
                                      />
                                    </a>
                                  </div>
                                </li>
                                <li
                                  className={`salon-information-box-list-item ${
                                    tabFocused === 'ownerInfo'
                                      ? 'is-current'
                                      : ''
                                  }`}
                                >
                                  <OwnerTab
                                    doorId={doorID}
                                    door={response.door}
                                    owner={response.owner}
                                    owned={response.owned}
                                    staff={response.staff}
                                  />
                                </li>
                                <li
                                  className={`salon-information-box-list-item ${
                                    tabFocused === 'samples' ? 'is-current' : ''
                                  }`}
                                >
                                  <div className="salon-information-more-box-wrap">
                                    {(() => {
                                      if (response?.door?.sample_posts.length) {
                                        return (
                                          <ul className="posts">
                                            {response.door.sample_posts.map(
                                              (sample_post) => (
                                                <React.Fragment
                                                  key={
                                                    sample_post.post_created_at
                                                  }
                                                >
                                                  {response.door && (
                                                    <SamplePostCardView
                                                      sample_post={sample_post}
                                                      setPostIdForDelete={
                                                        setPostIdForDelete
                                                      }
                                                      door={response.door}
                                                      owner={response.owner}
                                                      owned={response.owned}
                                                      staff={response.staff}
                                                      reloadData={() => {
                                                        initPage(doorID)
                                                      }}
                                                    />
                                                  )}
                                                  <SwipeableDrawer
                                                    anchor="bottom"
                                                    open={
                                                      postIdForDelete !== null
                                                    }
                                                    onClose={() => {
                                                      setPostIdForDelete(null)
                                                    }}
                                                    onOpen={() => {}}
                                                    PaperProps={{
                                                      style: {
                                                        borderTopLeftRadius:
                                                          '5px',
                                                        borderTopRightRadius:
                                                          '5px',
                                                        padding: '20px',
                                                        alignItems: 'center',
                                                        margin: '0 20px'
                                                      }
                                                    }}
                                                  >
                                                    {deleting ? (
                                                      <LoadingAnimation />
                                                    ) : (
                                                      <div className="delete-modal">
                                                        <div
                                                          onClick={
                                                            handleDeletePost
                                                          }
                                                          className="deleteModal-item delete"
                                                        >
                                                          <DeleteOutline />{' '}
                                                          投稿を削除する
                                                        </div>
                                                        <div
                                                          onClick={() =>
                                                            setPostIdForDelete(
                                                              null
                                                            )
                                                          }
                                                          className="deleteModal-item"
                                                        >
                                                          キャンセル
                                                        </div>
                                                      </div>
                                                    )}
                                                  </SwipeableDrawer>
                                                </React.Fragment>
                                              )
                                            )}
                                          </ul>
                                        )
                                      } else if (isRelations) {
                                        return (
                                          <div className="text-center mt5 mb5">
                                            投稿サンプルを設定しましょう
                                            <br />
                                            各投稿の「･･･」から設定できます
                                            <Link
                                              to={`/room/${response.owner.id}`}
                                              rel="nofollow"
                                              type="button"
                                              className="btn btn-block btn-size2 mt4"
                                            >
                                              投稿一覧を見る
                                            </Link>
                                          </div>
                                        )
                                      } else {
                                        return (
                                          <div className="emptyState -mypage">
                                            <p className="emptyState__title">
                                              投稿サンプルはありません
                                            </p>
                                            <p>更新をお待ち下さい</p>
                                          </div>
                                        )
                                      }
                                    })()}
                                  </div>
                                </li>
                                <li
                                  className={`salon-information-box-list-item ${
                                    tabFocused === 'reviews' ? 'is-current' : ''
                                  }`}
                                >
                                  {reviews === undefined && (
                                    <LoadingAnimation />
                                  )}
                                  {reviews !== undefined &&
                                    reviews.length > 0 &&
                                    reviews.map((review: Review, index) => {
                                      return (
                                        <Reviews key={`reviews_${index}`}>
                                          <ReviewsUserWrap>
                                            <ReviewsUserImage>
                                              <img
                                                src={
                                                  review.icon_image_url ||
                                                  DefaultUserIcon
                                                }
                                              />
                                            </ReviewsUserImage>
                                            <ReviewsUsernameWithEntriedWrap>
                                              <ReviewsUsername>
                                                {review.username}
                                              </ReviewsUsername>
                                              <ReviewsUserEntried>
                                                {dayjs(
                                                  review.entried_at
                                                ).format(formatMonth)}
                                                参加
                                              </ReviewsUserEntried>
                                            </ReviewsUsernameWithEntriedWrap>
                                          </ReviewsUserWrap>
                                          <ReviewsText>
                                            {review.body}
                                          </ReviewsText>
                                        </Reviews>
                                      )
                                    })}
                                  {reviews !== undefined &&
                                    reviews.length === 0 && (
                                      <div className="text-center mt5 mb5">
                                        <p>口コミが設定されていません</p>
                                        <Link
                                          to={
                                            response.staff
                                              ? `/door/${doorID}/management/reviews`
                                              : '/management/reviews'
                                          }
                                          rel="nofollow"
                                          type="button"
                                          className="btn btn-block btn-size2 mt4"
                                        >
                                          設定する
                                        </Link>
                                      </div>
                                    )}
                                  {additionalReviewsLoading ? (
                                    <LoadingAnimation />
                                  ) : (
                                    <Waypoint
                                      onEnter={reviewHandleScroll}
                                      scrollableAncestor="window"
                                    />
                                  )}
                                </li>
                              </ul>
                            </section>
                          </div>
                          <div className="salon-tab-sub-content">
                            {response.door.lottery && (
                              <ScratchBanner>
                                <ScratchBannerTitle>SCRATCH</ScratchBannerTitle>
                                <Link to={`/door/${doorID}/scratches/${response.door.lottery.id}`}>
                                  <ScratchBannerImage src={response.door.lottery.header_image ? response.door.lottery.header_image.url : ScratchBannerDummy } alt="" />
                                </Link>
                              </ScratchBanner>
                            )}
                            <section className="salon-plan">
                              <h3 className="salon-plan-title">
                                <span className="salon-plan-bold">PLAN</span>{' '}
                                LIST
                              </h3>
                              {/* <!--
                                salon-plan-content is-joined
                                加入済み
                              --> */}
                              {/* <!--通常プラン--> */}
                              <div
                                className="door-grid__item -right"
                                id="scroll-plans"
                              >
                                {plans?.length === 0 ? (
                                  <>
                                    <p
                                      className="door-grid-heading"
                                      style={{
                                        textAlign: 'center',
                                        marginTop: '32px'
                                      }}
                                    >
                                      このサロンで提供する内容や金額を
                                      <br />
                                      プランごとに設定できます
                                    </p>
                                    <Link
                                      to={planPath}
                                      rel="nofollow"
                                      type="button"
                                      className="btn btn-block btn-size2 mt4"
                                    >
                                      プランを作成する
                                    </Link>
                                  </>
                                ) : (
                                  <>
                                    {plans?.map((plan, index) => {
                                      return (
                                        <section
                                          className={classNames(
                                            'salon-plan-content',
                                            {
                                              'is-joined': plan.is_subscribing,
                                              'is-secretPlan': !!plan.secret_id
                                            }
                                          )}
                                        >
                                          <div className="salon-plan-content-inner">
                                            {plan.secret_id && (
                                              <div className="salon-plan-secretPlan-header">
                                                <img
                                                  src={SecretPlanHeader}
                                                  alt="SECRET PLAN"
                                                />
                                              </div>
                                            )}
                                            <div
                                              className={`${
                                                plan.thumnail_image
                                                  ? 'salon-plan-image'
                                                  : ''
                                              }`}
                                              style={{
                                                backgroundImage:
                                                  plan.thumnail_image
                                                    ? `url(${
                                                        plan.thumnail_image ||
                                                        ImageNormal
                                                      })`
                                                    : 'none'
                                              }}
                                            >
                                              {plan.is_subscribing && (
                                                <div className="salon-plan-image-info">
                                                  <img
                                                    src={IconCheckmark}
                                                    className="salon-plan-check"
                                                    alt="チェックマーク"
                                                  />
                                                  このプランで参加中
                                                </div>
                                              )}
                                            </div>
                                            <div className="salon-plan-content-title-box">
                                              <h4 className="salon-plan-content-title">
                                                {plan.title}
                                              </h4>
                                              {plan.joinable_starts_at &&
                                                plan.joinable_months &&
                                                plan.available_days_after_joinable && (
                                                  <>
                                                    <p className="salon-plan-content-title-period">
                                                      <span
                                                        className="salon-plan-icon"
                                                        style={{
                                                          backgroundImage: `url(${IconCalendar})`
                                                        }}
                                                      ></span>
                                                      {startDate(plan).format(
                                                        formatDay
                                                      )}
                                                      <span className="salon-plan-content-small">
                                                        {startDate(plan).format(
                                                          formatWeekDay
                                                        )}
                                                      </span>
                                                      ~
                                                      {endDate(plan).format(
                                                        formatDay
                                                      )}
                                                      <span className="salon-plan-content-small">
                                                        {endDate(plan).format(
                                                          formatWeekDay
                                                        )}
                                                      </span>
                                                      <br />
                                                      までの
                                                      {perMonth(
                                                        plan.joinable_months
                                                      )}
                                                      限定プラン
                                                    </p>
                                                  </>
                                                )}
                                            </div>
                                            {/* <!--salon-plan-text-box is-open で展開状態---> */}
                                            <div
                                              className={`salon-plan-text-box ${
                                                togglePlanDescriptions[index]
                                                  ? 'is-open'
                                                  : ''
                                              }`}
                                              ref={(el) =>
                                                (planRefs.current[index] = el)
                                              }
                                            >
                                              <div
                                                className={
                                                  planOmitted[index]
                                                    ? 'salon-plan-more-box'
                                                    : 'salon-plan-more-box -under'
                                                }
                                              >
                                                {planOmitted[index] ? (
                                                  <p className="salon-plan-content-text">
                                                    {plan.description}
                                                  </p>
                                                ) : (
                                                  <p className="salon-plan-content-text">
                                                    {plan.description}
                                                  </p>
                                                )}
                                              </div>
                                              {planOmitted[index] && (
                                                <>
                                                  <p
                                                    className="salon-plan-more-text"
                                                    onClick={() => {
                                                      togglePlanDescriptions[
                                                        index
                                                      ] = true
                                                      setTogglePlanDescriptions(
                                                        [
                                                          ...togglePlanDescriptions
                                                        ]
                                                      )
                                                    }}
                                                  >
                                                    もっと見る
                                                  </p>
                                                  <p
                                                    className="salon-plan-close-text"
                                                    onClick={() => {
                                                      togglePlanDescriptions[
                                                        index
                                                      ] = false
                                                      setTogglePlanDescriptions(
                                                        [
                                                          ...togglePlanDescriptions
                                                        ]
                                                      )
                                                    }}
                                                  >
                                                    閉じる
                                                  </p>
                                                </>
                                              )}
                                            </div>
                                            <div
                                              className={`salon-plan-bottom-box ${
                                                isRelations ? '-owner' : ''
                                              } ${
                                                plan.is_subscribing
                                                  ? 'is-no-button'
                                                  : ''
                                              }`}
                                            >
                                              <div className="salon-plan-price">
                                                {plan.free_trial_period && (
                                                  <span className="salon-plan-tag">
                                                    {freeTrialText(
                                                      plan.free_trial_period.toString()
                                                    )}
                                                  </span>
                                                )}
                                                {(() => {
                                                  if (
                                                    plan.joinable_starts_at &&
                                                    plan.joinable_months &&
                                                    plan.available_days_after_joinable
                                                  ) {
                                                    return (
                                                      <>
                                                        <span
                                                          className={`salon-plan-price-large`}
                                                        >
                                                          {plan.subscription_cost.toLocaleString()}
                                                        </span>
                                                        <span className="salon-plan-price-bold">
                                                          円
                                                        </span>
                                                        <div
                                                          style={{
                                                            display: 'flex',
                                                            alignItems:
                                                              'center',
                                                            justifyContent:
                                                              'center',
                                                            marginTop: '-16px',
                                                            marginBottom: '16px'
                                                          }}
                                                        >
                                                          <span className="salon-plan-price-bold">
                                                            {limitedPlanPriceSupplement(
                                                              plan.subscription_cost,
                                                              plan.joinable_months
                                                            )}
                                                          </span>
                                                          (税込)
                                                        </div>
                                                      </>
                                                    )
                                                  } else {
                                                    if (plan.recurring) {
                                                      return (
                                                        <>
                                                          <span
                                                            className={`salon-plan-price-large`}
                                                          >
                                                            {plan.subscription_cost.toLocaleString()}
                                                          </span>
                                                          <span className="salon-plan-price-bold">
                                                            円
                                                            {`/${perMonth(
                                                              plan.payment_interval_months
                                                            )}ごと`}
                                                          </span>
                                                          （税込）
                                                        </>
                                                      )
                                                    } else {
                                                      return (
                                                        <>
                                                          <span className="salon-plan-tag">
                                                            {`${perMonth(
                                                              plan.payment_interval_months
                                                            )}限定`}
                                                          </span>
                                                          <span
                                                            className={`salon-plan-price-large`}
                                                          >
                                                            {plan.subscription_cost.toLocaleString()}
                                                          </span>
                                                          <span className="salon-plan-price-bold">
                                                            円
                                                          </span>
                                                          （税込）
                                                        </>
                                                      )
                                                    }
                                                  }
                                                })()}
                                              </div>
                                              {!plan.is_suspended && (
                                                <>
                                                  {(plan.is_display_member_count ||
                                                    plan.capacity !== null) && (
                                                    <ul
                                                      className={`salon-plan-status-list ${
                                                        isRelations
                                                          ? '-owner'
                                                          : ''
                                                      } ${
                                                        plan.is_subscribing
                                                          ? 'is-no-button'
                                                          : ''
                                                      }`}
                                                    >
                                                      {(() => {
                                                        if (
                                                          plan.is_display_member_count
                                                        ) {
                                                          let name
                                                          let count
                                                          if (plan.reservable) {
                                                            name = '申し込み数'
                                                            count =
                                                              plan.reservation_count
                                                          } else {
                                                            name = 'メンバー数'
                                                            count =
                                                              plan.member_count
                                                          }
                                                          return (
                                                            <li className="salon-plan-status-item">
                                                              <span
                                                                className="salon-plan-icon"
                                                                style={{
                                                                  backgroundImage: `url(${IconUser})`
                                                                }}
                                                              ></span>
                                                              {name}
                                                              <span className="salon-plan-status-bold">
                                                                {count}
                                                              </span>
                                                              名
                                                            </li>
                                                          )
                                                        }
                                                      })()}
                                                      {(() => {
                                                        if (
                                                          plan.capacity !== null
                                                        ) {
                                                          // member_countはメンバー表示フラグの状態でnullか数値がはいる
                                                          // nullじゃなく、かつ人数上限に達していない場合のみ定員表示をON
                                                          return (
                                                            <>
                                                              <li className="salon-plan-status-item">
                                                                {!plan.is_display_member_count && (
                                                                  <span
                                                                    className="salon-plan-icon"
                                                                    style={{
                                                                      backgroundImage: `url(${IconUser})`
                                                                    }}
                                                                  />
                                                                )}
                                                                <span className="salon-plan-status-bold">
                                                                  定員{' '}
                                                                  {
                                                                    plan.capacity
                                                                  }
                                                                </span>
                                                                名
                                                              </li>
                                                            </>
                                                          )
                                                        }
                                                      })()}
                                                    </ul>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                            {isRelations &&
                                              plan.is_suspended && (
                                                <button
                                                  style={{ border: 'none' }}
                                                  className="button button-gra2 is-disable"
                                                  disabled={true}
                                                >
                                                  募集停止中
                                                </button>
                                              )}
                                            {!isRelations &&
                                              !plan.is_subscribing && (
                                                <button
                                                  style={{ border: 'none' }}
                                                  className={`button button-gra2 ${
                                                    plan.is_subscribing ||
                                                    plan.is_reserving ||
                                                    plan.is_suspended ||
                                                    plan.is_capacity_limit
                                                      ? 'is-disable'
                                                      : ''
                                                  }`}
                                                  onClick={async (
                                                    event: any
                                                  ) => {
                                                    if (
                                                      plan.is_subscribing ||
                                                      isRelations
                                                    ) {
                                                      // オーナーもしくはサブスク済みはルームへ遷移
                                                      history.push(
                                                        `/room/${response?.owner.id}`
                                                      )
                                                    } else {
                                                      // プラン一覧へ
                                                      pushHistoryWithSecret(
                                                        `/door/${response?.owner.id}/plans?select=${plan.id}`
                                                      )
                                                    }
                                                  }}
                                                >
                                                  {planButtonText(plan)}
                                                </button>
                                              )}
                                          </div>
                                        </section>
                                      )
                                    })}
                                  </>
                                )}
                              </div>
                              <div className="salon-information">
                                <div className="salon-information-banner-box tb-only">
                                  <a
                                    href="https://join-howto.yoor.jp/?_gl=1*1ceelqa*_ga*MTY5NDgwOTI3MC4xNjU5MzQwMDU2*_ga_18HYVMJFLJ*MTY5OTYwMzQ1Ni4xMTA0LjEuMTY5OTYwMzQ2NC41Mi4wLjA."
                                    className="salon-information-banner"
                                    target="_blank"
                                  >
                                    <img
                                      src={BannerSalonBeginner}
                                      className="salon-information-banner-image"
                                      alt="初めての方へ　サロン入会の方法はこちら"
                                    />
                                  </a>
                                </div>
                              </div>
                            </section>
                          </div>
                        </div>
                      </div>
                    </SalonTab>
                  </SalonTop>
                )
              } else {
                return (
                  <ProfileContainer>
                    <div className="profile">
                      <div className="profile-cover -header">
                        <img
                          src={response.owner.header}
                          onError={(e) => {
                            let target = e.target as HTMLImageElement
                            target.src = DummyHeader
                          }}
                          alt=""
                        />
                      </div>
                      <div className="profile-main">
                        <div className="profile-user">
                          <div className="profile-introduce">
                            <div className="profile-introduce__item -top">
                              <div className="avatar">
                                <div
                                  className="icon"
                                  style={{
                                    backgroundImage: `url(${
                                      response.owner.icon
                                        ? response.owner.icon
                                        : DefaultUserIcon
                                    })`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: '50% 50%',
                                    backgroundRepeat: 'no-repeat'
                                  }}
                                ></div>
                              </div>
                              <div className="profile-introduce__inner">
                                {response.door && (
                                  <div className="title">
                                    <span>{response.door.title}</span>
                                  </div>
                                )}
                                <div className="username">
                                  {response.owner.name}
                                </div>
                              </div>
                            </div>
                            <div className="profile-introduce__item -bottom">
                              <div className="profile-detail">
                                <div className="description">
                                  <Linkify
                                    componentDecorator={(
                                      decoratedHref,
                                      decoratedText
                                    ) => (
                                      <a
                                        style={{ color: '#0994ca' }}
                                        target="_blank"
                                        href={decoratedHref}
                                      >
                                        {decoratedText}
                                      </a>
                                    )}
                                  >
                                    {response.owner.description}
                                  </Linkify>
                                </div>
                                <div className="profile-detailInner">
                                  <div className="profile-detailInner__item">
                                    {response.door && (
                                      <>
                                        <dl>
                                          <dt>投稿</dt>
                                          <dd>{response.door.post_count}</dd>
                                        </dl>
                                        {response.door
                                          .latest_post_updated_at && (
                                          <dl>
                                            <dt>更新</dt>
                                            <dd>
                                              {
                                                response.door
                                                  .latest_post_updated_at
                                              }
                                            </dd>
                                          </dl>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {response.door && (
                          <>
                            {response.door.member_count !== null && ( // オーナーであるか、メンバー数表示オンの時のみメンバー数が返ってくる
                              <dl className="displayMember">
                                <dt>メンバー</dt>
                                <dd>
                                  {(() => {
                                    const member_count = (
                                      <>
                                        <span>
                                          {response.door.member_count}
                                        </span>
                                        人
                                      </>
                                    )
                                    if (isRelations) {
                                      // オーナーの時のみメンバー一覧へのリンクとする
                                      return (
                                        <Link to={memberPath} rel="nofollow">
                                          {member_count}
                                        </Link>
                                      )
                                    } else {
                                      return member_count
                                    }
                                  })()}
                                </dd>
                              </dl>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </ProfileContainer>
                )
              }
            })()}
          </>
        )
      )}
      <SimpleModal
        open={urlSignatureErrorDialogOpening}
        onCloseButtonClick={() => {
          setUrlSignatureErrorDialogOpening(false)
        }}
        enableClosingOnOutsideClick={false}
      >
        URLの有効期限が切れています。 <br />
        お手数ですが、サロンオーナーにURLの再発行依頼をお願いします。
      </SimpleModal>
      <Dialog
        onClose={() => setShowNotificationDialog(false)}
        open={showNotificationDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <NotificationPermissionDialog
          close={() => setShowNotificationDialog(false)}
          callback={grantedCallback}
        />
      </Dialog>
      {deniedInvitation && (
        <InvitationNotificationModal
          invitation={deniedInvitation}
          setInvitation={setDeniedInvitation}
          onClose={() => removeInvitationToken()}
        />
      )}
      {location.state?.showContentAccessDialog && (
        <ContentAccessDialog
          open={contentAccessDialogOpening}
          onClose={() => setContentAccessDialogOpening(false)}
          doorId={doorID}
          roomId={location.state?.roomId}
          type={ isSignIn ? 'plan' : 'login' }
          callBackUrl={location.state?.callBackUrl}
        />
      )}
    </React.Fragment>
  )
}

// サロン紹介タブ
function IntroduceTab(props: {
  door: DoorDetail
  owned: boolean
  staff: string | null
  doorId: string
}) {
  const [showButton, setShowButton] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const salonDetailRef = useRef<HTMLParagraphElement | null>(null)
  const is_relations = props.owned || props.staff
  const configPath = props.staff
    ? `/door/${props.doorId}/management/door/edit`
    : '/management/door/edit'

  const handleClickSalonDetailMore = (event: React.MouseEvent<HTMLElement>) => {
    salonDetailRef.current?.classList.add('is-open')
  }

  const handleClickSalonDetailClose = () => {
    salonDetailRef.current?.classList.remove('is-open')
  }

  useEffect(() => {
    if (
      !showButton &&
      ref &&
      ref.current &&
      ref.current.getBoundingClientRect().height > 800
    ) {
      setShowButton(true)
    }
  }, [])
  if (
    props.door.door_images.length >= 1 &&
    props.door.door_images[0].description
  ) {
    return (
      <div
        ref={salonDetailRef}
        className={`salon-information-more-box-wrap ${
          showButton ? '' : 'is-open'
        }`}
      >
        {/* salon-information-more-box is-open で開く */}
        <div className="salon-information-more-box">
          <div className="salon-information-content-box">
            <div ref={ref}>
              {props.door.external_video_link !== '' && (
                <div
                  className="mb2 intro_movie_wrap"
                  style={{ paddingRight: '1%', position: 'relative' }}
                >
                  <iframe
                    className="intro_movie"
                    src={props.door.external_video_link}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    style={{ width: '100%', aspectRatio: '16/9' }}
                  />
                </div>
              )}
              {props.door.door_images.map((image) => (
                <React.Fragment key={image.created_at}>
                  {image.image_url && (
                    <div
                      className="salon-information-image"
                      // style={{ backgroundImage: `url(${image.image_url})` }}
                      onLoad={() => {
                        if (
                          !showButton &&
                          ref &&
                          ref.current &&
                          ref.current.getBoundingClientRect().height > 200
                        ) {
                          setShowButton(true)
                        }
                      }}
                    >
                      <img src={image.image_url} alt="" />
                    </div>
                  )}
                  <div
                    className="salon-information-content-text"
                    key={image.created_at}
                  >
                    <Linkify
                      key={image.description}
                      componentDecorator={(decoratedHref, decoratedText) => (
                        <a
                          key={decoratedHref}
                          style={{ color: '#0994ca' }}
                          target="_blank"
                          href={decoratedHref}
                        >
                          {decoratedText}
                        </a>
                      )}
                    >
                      {image.description}
                    </Linkify>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        {showButton && (
          <>
            <p
              className="salon-information-more-text"
              onClick={handleClickSalonDetailMore}
            >
              もっと見る
            </p>
            <p
              className="salon-information-close-text"
              onClick={handleClickSalonDetailClose}
            >
              閉じる
            </p>
          </>
        )}
      </div>
    )
  } else if (is_relations) {
    return (
      <div className="text-center mt5 mb5">
        ここにはサロンの紹介文が表示されます
        <br />
        このサロンで何を行うか、どんなサロンにしたいかを伝えましょう
        <Link
          to={configPath}
          rel="nofollow"
          type="button"
          className="btn btn-block btn-size2 mt4"
        >
          設定する
        </Link>
      </div>
    )
  } else {
    return (
      <div className="emptyState -mypage">
        <p className="emptyState__title">サロン紹介はありません</p>
        <p>更新をお待ち下さい</p>
      </div>
    )
  }
}

// オーナー情報タブ
function OwnerTab(props: {
  door: DoorDetail
  owner: User
  owned: boolean
  staff: string | null
  doorId: string
}) {
  const basePath = '/management/owner/edit'
  const is_relations = props.owned || props.staff
  const configPath = props.staff ? `/door/${props.doorId}${basePath}` : basePath
  if (props.owner.description) {
    return (
      <>
        {props.door.door_owners.length > 0 &&
          props.door.door_owners[0].image_url &&
          props.door.door_owners.map(
            (owner) =>
              owner.image_url && (
                <div
                  className="salon-information-image"
                  // style={{ backgroundImage: `url(${owner.image_url})` }}
                >
                  <img src={owner.image_url} alt="" />
                </div>
              )
          )}
        <div className="salon-information-more-box-wrap">
          <div
            className="salon-information-more-box -blurNone"
            style={{ maxHeight: '100%' }}
          >
            <div className="salon-information-content-box">
              <h4 className="salon-information-content-title">
                {props.owner.name}
              </h4>
              <p className="salon-information-content-text">
                <Linkify
                  componentDecorator={(decoratedHref, decoratedText) => (
                    <a
                      style={{ color: '#0994ca' }}
                      target="_blank"
                      href={decoratedHref}
                    >
                      {decoratedText}
                    </a>
                  )}
                >
                  {props.owner.description}
                </Linkify>
              </p>
            </div>
          </div>
        </div>
      </>
    )
  } else if (is_relations) {
    return (
      <div className="text-center mt5 mb5">
        あなたの経歴やプロフィールを書きましょう
        <Link
          to={configPath}
          rel="nofollow"
          type="button"
          className="btn btn-block btn-size2 mt4"
        >
          設定する
        </Link>
      </div>
    )
  } else {
    return (
      <div className="emptyState -mypage">
        <p className="emptyState__title">オーナー情報はありません</p>
        <p>更新をお待ち下さい</p>
      </div>
    )
  }
}

function SamplePostCardView(props: {
  door: DoorDetail
  owner: User
  owned: boolean
  staff: string | null
  sample_post: SamplePost
  setPostIdForDelete: (post_id: string) => void
  reloadData: Function
}) {
  let { door_id } = useParams<{
    door_id: string
  }>()
  const classes = makeStyles((theme) => ({
    media: {
      height: 0,
      paddingTop: '56.25%', // 16:9
      marginBottom: theme.spacing(1),
      borderRadius: '7px'
    },
    simpleMenu: {
      padding: 0,
      '& ul': {
        padding: 0,
        '& li': {
          fontSize: '13px'
        }
      }
    }
  }))()
  const dispatch = useDispatch()
  const {
    door,
    owner,
    owned,
    staff,
    sample_post,
    setPostIdForDelete,
    reloadData
  } = props
  const is_relations = owned || staff
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isOpenEditSamplePostDialog, setIsOpenEditSamplePostDialog] =
    useState<boolean>(false)

  const documentClickHandler = (event: any) => {
    removeDocumentClickHandler()
    handleClose()
  }
  const removeDocumentClickHandler = () => {
    document.removeEventListener('click', documentClickHandler)
  }
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // 親要素のaタグを挙動を止める
    event.preventDefault()
    document.body.addEventListener('click', documentClickHandler)
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <li key={sample_post.dynamo_id}>
      <StyledLink to={`/door/${owner.id}/samples/${sample_post.id}`}>
        <SamplePostHeader>
          <SamplePostHeaderItem>
            {sample_post.is_restricted && (
              <MemberOnlyBadge>会員限定</MemberOnlyBadge>
            )}
            <SamplePostTitle>{sample_post.title}</SamplePostTitle>
          </SamplePostHeaderItem>
          {is_relations && (
            <SamplePostHeaderItem>
              <div className="action">
                <span
                  className="material-icons modal-open"
                  aria-controls="simple-menu"
                  aria-haspopup="true"
                  onClick={handleClick}
                >
                  <MoreHoriz />
                </span>
                <Menu
                  id="simple-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  className={classes.simpleMenu}
                >
                  {/* MenuItemのクリックイベントがaタグに伝播して画面遷移してしまうので防ぐため */}
                  <div onClick={(event) => event.stopPropagation()}>
                    <MenuItem
                      onClick={() => {
                        setIsOpenEditSamplePostDialog(true)
                      }}
                    >
                      投稿サンプルを編集
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setPostIdForDelete(sample_post.dynamo_id)
                      }}
                    >
                      <span style={{ color: '#ff0000' }}>削除</span>
                    </MenuItem>
                  </div>
                </Menu>
              </div>
            </SamplePostHeaderItem>
          )}
        </SamplePostHeader>

        <SmaplePostImageWithText>
          {(sample_post.attached_files.images.length > 0 ||
            sample_post.attached_files.videos.length > 0) && (
            <SamplePostImage>
              {/* TODO: サンプル投稿は複数枚対応せず1枚のみ。 */}
              {sample_post.attached_files.images.length > 0 && (
                <ImagesPreviewWrap>
                  {sample_post.is_restricted && <StyledLockIcon />}
                  <ImagesPreview images={sample_post.attached_files.images} />
                </ImagesPreviewWrap>
              )}
              <SamplePostFilesPreview
                attachedFiles={sample_post.attached_files}
                canDownload={false}
              />
              {sample_post.attached_files.videos.length > 0 && (
                <ImagesPreview
                  images={sample_post.attached_files.videos.map(
                    (video, index) => {
                      return {
                        id: index,
                        url: video.url?.replace('.m3u8', '.0000000.jpg')
                      }
                    }
                  )}
                />
              )}
            </SamplePostImage>
          )}
          <SamplePostText>
            <Linkify
              componentDecorator={(decoratedHref, decoratedText) => (
                <a
                  style={{ color: '#0994ca' }}
                  target="_blank"
                  onClick={(e) =>
                    // 親要素のaタグを挙動を止める
                    e.stopPropagation()
                  }
                  href={decoratedHref}
                >
                  {decoratedText}
                </a>
              )}
            >
              {sample_post.content.length >= 100
                ? `${sample_post.content}...`
                : sample_post.content}
            </Linkify>
          </SamplePostText>
        </SmaplePostImageWithText>
      </StyledLink>

      <SamplePostEditModal
        isOpen={isOpenEditSamplePostDialog}
        onClose={() => {
          setIsOpenEditSamplePostDialog(false)
        }}
        postDynamoId={sample_post.dynamo_id}
        contentCharCount={sample_post.content_char_count}
        callback={reloadData}
      />

      <UserInfo>
        <UserInfoItemLeft>
          <UserIcon
            src={sample_post.writer_user_public.icon || DefaultUserIcon}
          />
          <UserNameWithPostDate>
            <UserName>{sample_post.writer_user_public.name}</UserName>
            <UserPostDate>
              {dateUtils.dateConvertiongForView(sample_post.post_created_at)}
            </UserPostDate>
          </UserNameWithPostDate>
        </UserInfoItemLeft>
        <UserInfoItemRight>
          <SamplePostMore to={`/door/${owner.id}/samples/${sample_post.id}`}>
            {'続きを読む'}
          </SamplePostMore>
        </UserInfoItemRight>
      </UserInfo>
    </li>
  )
}
export default Index

const StyledLink = styled(Link)`
  .action {
    svg {
      color: #333;
    }
  }
`
const ImagesPreviewWrap = styled.div`
  position: relative;
`
const StyledLockIcon = styled(LockIcon)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  width: auto !important;
  height: 40px;
  color: #fff;
`

const MemberOnlyBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 28px;
  border: 1px solid #45b5f5;
  color: #45b5f5;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
`
const SamplePostHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  @media (max-width: 640px) {
    gap: 12px;
  }
`
const SamplePostHeaderItem = styled.div``
const SamplePostTitle = styled.h2`
  color: #333;
  font-size: 22px;
  margin-bottom: 8px;
`
const SamplePostText = styled.p`
  font-size: 14px;
  font-weight: normal;
  color: #333;
  white-space: pre-wrap;
  line-height: 1.8;
  @media (max-width: 640px) {
    margin-bottom: 16px;
  }
`
const SmaplePostImageWithText = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  @media (max-width: 640px) {
    flex-direction: column;
  }
`
const SamplePostImage = styled.div`
  width: 136px;
  height: 76px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  @media (max-width: 640px) {
    width: auto;
    height: auto;
    aspect-ratio: 179/80;
    margin-bottom: 0;
  }
  .media {
    margin: 0;
  }
  img {
    width: 136px;
    height: 76px;
    object-fit: cover;
    @media (max-width: 640px) {
      aspect-ratio: 179/80;
      width: 100%;
      height: auto;
    }
  }
`

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`
const UserInfoItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const UserInfoItemRight = styled.div`
  flex-shrink: 0;
`
const UserIcon = styled.img`
  width: 42px;
  height: 42px;
  object-fit: cover;
  border-radius: 100%;
`
const UserNameWithPostDate = styled.div`
  line-height: 1.5;
`
const UserName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
`
const UserPostDate = styled.div`
  font-size: 12px;
  color: #666;
`
const SamplePostMore = styled(Link)`
  font-size: 16px;
  color: #45b5f5;
`

const BreadCrumbsStyled = styled(BreadCrumbs)`
  margin-top: 16px;
  margin-bottom: 0;
  @media (max-width: 640px) {
    padding: 0 16px;
  }
  ul {
    @media (max-width: 640px) {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    li {
      @media (max-width: 640px) {
        &:not(:first-child) {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
      a,
      span {
        max-width: none;
      }
    }
  }
`

const ProfileContainer = styled.div`
  width: 700px;
  max-width: 700px;
  margin: 32px auto 0;
  @media (max-width: 640px) {
    width: 100%;
    margin: 0 auto;
  }
`

const SalonTop = styled.section`
  font-family: 'Noto Sans JP', sans-serif;
  line-height: 1.9;
  font-size: 13px;
  background-color: #ffffff;
  color: #333;
  -webkit-font-smoothing: antialiased;
  font-weight: 500;

  &::-moz-selection {
    color: #ffffff;
    background-color: #26bfdb;
  }
  &::selection {
    color: #ffffff;
    background-color: #26bfdb;
  }
  .text-blue {
    color: #26bfdb;
  }
  .text-pink {
    color: #fc4db9;
  }
  .text-red {
    color: #f03b6b;
  }
  .text-yellow {
    color: #ffff53;
  }
  .text-bold {
    font-weight: bold;
  }
  .tb-only {
    display: none;
  }
  @media (max-width: 768px) {
    .tb-only {
      display: block;
    }
  }
  .pc-only {
    display: block;
  }
  @media (max-width: 768px) {
    .pc-only {
      display: none;
    }
  }

  .button {
    cursor: pointer;
    &.button-blue {
      background-color: #26bfdb;
      box-shadow: 0 12px 32px rgba(38, 191, 219, 0.24);
      border: 1px solid #ffffff;
      width: 378px;
      height: 72px;
      border-radius: 36px;
      display: -webkit-box;
      display: flex;
      color: #ffffff;
      align-items: center;
      font-size: 16px;
      -webkit-transition: 0.3s ease;
      transition: 0.3s ease;
      font-weight: bold;
      &:hover {
        background-color: #ffffff;
        color: #26bfdb;
        .button-icon-arrow::before {
          background-image: url(&{IconArrow});
        }
      }
      &.is-text-center {
        text-align: center;
        .button-icon-arrow {
          padding: 0;
        }
      }
    }
    &.button-icon-arrow {
      position: relative;
      display: block;
      text-align: center;
      width: 100%;
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        margin: auto;
        right: 8px;
        width: 14px;
        height: 14px;
        background-image: url(${IconArrow});
        background-repeat: no-repeat;
        background-size: contain;
        transition: 0.3s ease;
      }
    }
    &.button-tag-list {
      display: -webkit-box;
      display: flex;
      flex-wrap: wrap;
      @media (max-width: 768px) {
        justify-content: center;
      }
    }
    &.button-tag-item {
      margin-bottom: 8px;
      margin-top: 8px;
      margin-left: 7px;
      margin-right: 7px;
      @media (max-width: 768px) {
        margin-bottom: 4px;
        margin-top: 4px;
        margin-left: 2px;
        margin-right: 2px;
      }
    }
    &.button-tag {
      display: inline-block;
      height: 35px;
      border: 1px solid #26bfdb;
      border-radius: 17.5px;
      color: #26bfdb;
      padding: 0 22px;
      line-height: 33px;
      transition: 0.3s ease;
      &:hover {
        background-color: #26bfdb;
        color: #ffffff;
      }
      & + .button-tag {
        margin-left: 12px;
      }
    }
    &.button-gra2 {
      width: 100%;
      height: 48px;
      border-radius: 24px;
      background: rgb(25, 152, 208);
      background: linear-gradient(
        140deg,
        rgb(25, 152, 208) 0%,
        rgb(102, 242, 210) 100%
      );
      display: inline-block;
      line-height: 48px;
      color: #ffffff;
      position: relative;
      font-size: 15px;
      text-align: center;
      cursor: pointer;
      transition: 0.3s ease;
      background-color: #26bfdb;
      font-weight: bold;
      &:hover {
        background-color: #ffffff;
        background: #ffffff;
        color: #26bfdb;
        box-shadow: 0 4px 24px rgba(107, 133, 141, 0.16);
      }
      &.is-disable {
        background: #989898;
        pointer-events: none;
      }
    }
    &.button-arrow-box {
      margin-top: 24px;
    }
    .button-arrow-box-inner {
      width: 180px;
      margin: auto;
      display: -webkit-box;
      display: flex;
      justify-content: space-between;
    }
    .button-slide-page {
      font-size: 13px;
      font-family: 'Quicksand';
    }
    .button-slide-arrow-left {
      width: 24px;
      height: 24px;
      background-size: cover;
      background-image: url(${BtnArrowLeftGray});
      pointer-events: none;
      transition: 0.3s ease;
      &:hover {
        opacity: 0.4;
      }
      &.is-active {
        background-image: url(${BtnArrowLeft});
        pointer-events: all;
      }
    }
    .button-slide-arrow-right {
      width: 24px;
      height: 24px;
      background-size: cover;
      background-image: url(${BtnArrowRightGray});
      pointer-events: none;
      transition: 0.3s ease;
      &:hover {
        opacity: 0.4;
      }
      &.is-active {
        background-image: url(${BtnArrowRight});
        pointer-events: all;
      }
    }
    .button-slide-bold {
      font-weight: bold;
    }
  }
  .share {
    &.share-side {
      position: absolute;
      bottom: 120px;
      left: 8px;
      background-color: #ffffff;
    }
    .share-title {
      font-family: 'Montserrat';
      font-weight: 500;
      color: #797979;
      font-size: 13px;
      transform: rotate(90deg);
      position: relative;
      left: -5px;
    }
    .share-image {
      width: 35px;
      @media (max-width: 640px) {
        width: 28px;
        height: 28px;
      }
    }
  }
`
const SalonTopFixedBox = styled.div`
  display: none;
  z-index: 5;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
  }
  .button {
    &.button-blue {
      height: 48px;
      border-radius: 24px;
      line-height: 48px;
      font-size: 13px;
      width: calc(100% - 58px);
    }
  }
  .button-icon-arrow {
    height: 48px;
    border-radius: 24px;
    &::before {
      top: 18px;
      right: 20px;
    }
  }
  .salon-top-fixed-box-inner {
    padding: 12px;
    display: flex;
    justify-content: space-between;
  }
  .salon-top-sns-button {
    width: 48px;
    height: 48px;
    border-radius: 24px;
    border: 1px solid #dae3e3;
    background-color: #ffffff;
    background-image: url(${IconShare});
    background-size: 65%;
    background-position: center;
    background-repeat: no-repeat;
    &.is-open {
      background-color: #26bfdb;
      background-image: url(${IconShareWhite});
      & + .share-side {
        position: absolute;
        top: auto;
        left: auto;
        right: 7px;
        bottom: 75px;
        width: 56px;
        height: 223px;
        border-radius: 12px;
        border: 1px solid #26bfdb;
        box-shadow: 0 12px 24px rgba(38, 191, 219, 0.24);
        .share-title {
          left: 0;
          top: 30px;
        }
        .share-link {
          margin: auto;
        }
        .share-list {
          margin-top: 50px;
        }
      }
    }
  }
`

const SalonTopTopBg = styled.div`
  width: calc(100% + 40px);
  height: 152px;
  margin-left: -20px;
  background-position: center center;
  background-size: cover;
  position: relative;
  display: none;
  @media (max-width: 768px) {
    display: block;
    width: 100%;
    margin-left: 0;
    padding-top: 57%;
    height: auto;
  }
  .salon-top-filter {
    backdrop-filter: blur(30px);
    height: 152px;
    @media (max-width: 768px) {
      backdrop-filter: blur(0);
      height: auto;
    }
  }
`

const SalonTopInner = styled.div`
  max-width: 1280px;
  margin: auto;
  .salon-top-user-image {
    width: 64px;
    height: 64px;
    border-radius: 32px;
    background-size: cover;
    flex-shrink: 0;
    background-position: 50% 50%;
    background-repeat: no-repeat;
  }
  .salon-top-user-image-box {
    display: flex;
    align-items: center;
    margin-top: 12px;
    margin-bottom: 20px;
  }
  .salon-top-name {
    font-size: 13px;
    margin-left: 16px;
  }
  .salon-top-small-text {
    font-size: 10px;
    font-weight: 300;
  }
  .salon-top-date {
    font-size: 12px;
    margin-left: 26px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    line-height: 1;
    gap: 4px;
    @media (max-width: 768px) {
      position: absolute;
      left: -24px;
      top: 56px;
    }
  }
  .salon-top-content {
    margin-top: 16px;
    padding-bottom: 54px;
    display: flex;
    justify-content: space-between;
    @media (max-width: 768px) {
      margin-top: 12px;
      padding: 0 16px 30px;
      display: block;
    }
  }
  .salon-top-text-box {
    width: 42%;
    @media (max-width: 768px) {
      width: 100%;
    }
  }
  .salon-top-sub-title {
    font-size: 24px;
    font-weight: bold;
    line-height: 1.5;
    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
  .salon-top-text {
    font-size: 15px;
    margin-top: 20px;
    font-weight: normal;
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
  .salon-top-bottom-box {
    border-top: 1px solid #dae3e3;
    margin-top: 17px;
    padding-top: 12px;
    position: relative;
    @media (max-width: 768px) {
      margin-top: 24px;
    }
  }
  .salon-top-status-list {
    display: flex;
    align-items: center;
  }
  .salon-top-status-item {
    padding: 0 0 0 32px;
    position: relative;
    font-size: 13px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    line-height: 1;
    & + .salon-top-status-item {
      margin-left: 48px;
      @media (max-width: 768px) {
        margin-left: 26px;
      }
    }
  }
  .salon-top-icon {
    width: 24px;
    height: 24px;
    background-repeat: no-repeat;
    background-size: 100%;
    display: inline-block;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
  }
  .salon-top-status-text {
    font-weight: 400;
  }
  .salon-top-number {
    font-weight: bold;
    display: flex;
    align-items: center;
    line-height: 1;
    a {
      color: #333;
    }
  }
  .salon-top-status-large {
    font-size: 18px;
    display: inline-block;
    margin-left: 12px;
    margin-right: 3px;
    line-height: 1.1;
    a {
      line-height: 1.1;
    }
    @media (max-width: 768px) {
    }
  }
  .salon-top-main-image-wrap {
    width: 55%;
    @media (max-width: 768px) {
      display: none;
    }
  }
  .salon-top-main-image {
    background-position: center;
    background-size: cover;
    padding-top: 58%;
    box-shadow: 32px 32px 32px rgba(37, 80, 88, 0.24);
  }
  .salon-top-button-box {
    margin-top: 40px;
    .button {
      margin: auto;
    }
  }
  .salon-top-title-red {
    font-size: 15px;
    padding-left: 11px;
    color: #f03b6b;
    font-weight: bold;
    border-left: 4px solid #f03b6b;
    line-height: 25px;
    margin-bottom: 8px;
    display: block;
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
`

const SalonTab = styled.div`
  padding: 0 20px 0;
  @media (max-width: 768px) {
    padding: 0 0 0;
  }
  .salon-tab-anker {
    position: absolute;
    transform: translateY(-80px);
    visibility: hidden;
    @media (max-width: 768px) {
      transform: translateY(0);
    }
  }
  .salon-tab-inner {
    border-top: 2px solid #dae3e3;
    max-width: 1280px;
    padding: 54px 0 110px;
    margin: auto;
    display: flex;
    @media (max-width: 768px) {
      border-top: 0;
      display: block;
      padding: 54px 0 0;
    }
  }
  .salon-tab-list-wrap {
    width: 158px;
    position: relative;
    @media (max-width: 768px) {
      width: 100%;
    }
  }
  .salon-tab-list {
    width: 158px;
    position: relative;
    @media (max-width: 768px) {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      background-color: #ffffff;
    }
  }
  .salon-tab-list-inner {
    height: 100vh;
    min-height: 600px;
    position: relative;
    @media (max-width: 768px) {
      width: 100%;
      height: auto;
      min-height: 0;
      display: -webkit-box;
      display: flex;
      padding: 0 16px;
      background-color: #ffffff;
    }
    &.is-fixed {
      position: fixed;
      top: 100px;
      z-index: 1;
      @media (max-width: 768px) {
        top: 46px;
      }
    }
    &.is-bottom {
      position: absolute;
      top: auto;
      bottom: -200px;
      left: 0;
      @media (max-width: 768px) {
        position: fixed;
        top: 46px;
        bottom: unset;
        z-index: 10;
      }
    }
  }
  .salon-tab-item {
    @media (max-width: 768px) {
      width: 50%;
      text-align: center;
    }
    &.is-current .salon-tab-link {
      color: #26bfdb;
    }
    & + .salon-tab-item {
      margin-top: 18px;
      @media (max-width: 768px) {
        margin-top: 0;
      }
    }
  }
  .salon-tab-link {
    font-size: 14px;
    color: #989898;
    @media (max-width: 768px) {
      font-size: 13px;
      line-height: 42px;
      display: block;
      width: 100%;
    }
    &.is-current {
      color: #26bfdb;
      font-weight: bold;
      @media (max-width: 768px) {
        border-bottom: 2px solid #26bfdb;
      }
    }
  }
  .salon-tab-content {
    display: flex;
    justify-content: space-between;
    width: calc(100% - 158px);
    @media (max-width: 768px) {
      display: block;
      width: 100%;
    }
    &.is-fixed-tab-list {
      @media (max-width: 768px) {
        /* margin-top: 45px; */
      }
    }
  }
  .salon-tab-main-content {
    width: calc(100% - 399px);
    @media (max-width: 768px) {
      width: 100%;
      padding: 35px 16px 36px;
    }
  }
  .salon-tab-sub-content {
    width: 345px;
    @media (max-width: 768px) {
      width: 100%;
      background-color: #f6f7f7;
      padding: 35px 16px 120px;
    }
  }
  .salon-information {
    .salon-information-box-list-item {
      display: none;
      margin-top: 18px;
      @media (max-width: 768px) {
        margin-top: 0;
      }
      &.is-current {
        display: block;
      }
    }
    .salon-information-title {
      font-family: 'Montserrat';
      font-weight: 300;
      font-size: 16px;
      @media (max-width: 768px) {
        display: none;
      }
    }
    .salon-information-bold {
      font-family: 'Montserrat';
      font-weight: 700;
      margin-right: 6px;
    }
    .salon-information-image {
      text-align: center;
    }
    .salon-information-image img {
      max-width: 100%;
      max-height: 400px;
      vertical-align: bottom;
      @media (max-width: 768px) {
        max-width: 100%;
      }
    }
    .salon-information-content-box {
      margin-top: 24px;
      @media (max-width: 768px) {
        margin-top: 0;
      }
      & + .salon-information-content-box {
        position: relative;
        border-top: 2px solid #dae3e3;
        margin-top: 50px;
        &::before {
          content: '';
          position: absolute;
          width: 172px;
          height: 2px;
          background-color: #26bfdb;
        }
        .salon-information-content-title {
          margin-top: 30px;
        }
      }
    }
    .salon-information-content-title {
      font-size: 24px;
      font-weight: bold;
      margin-top: 40px;
      line-height: 1.45;
    }
    .salon-information-content-text {
      margin-top: 30px;
      margin-bottom: 30px;
      font-size: 16px;
      line-height: 1.6;
      white-space: pre-line;
      font-weight: normal;
    }
    .salon-information-banner-box {
      margin-top: 80px;
    }
    .salon-information-banner {
      max-width: 540px;
      display: block;
      margin: auto;
      transition: 0.3s ease;
      @media (max-width: 768px) {
        max-width: 100%;
      }
      &:hover {
        opacity: 0.4;
      }
    }
    .salon-information-banner-image {
      width: 100%;
      vertical-align: bottom;
    }
    .salon-information-red-text {
      color: #f03b6b;
      font-weight: bold;
    }
    .salon-information-more-box-wrap {
      &.is-open {
        .salon-information-more-box {
          max-height: 100%;
          &::before {
            display: none;
          }
        }
        .salon-information-more-text {
          display: none;
        }
        .salon-information-close-text {
          display: block;
        }
      }
    }
    .salon-information-more-box {
      &::before {
        display: none;
        @media (max-width: 768px) {
          content: '';
          width: 100%;
          bottom: 0px;
          position: absolute;
          height: 64px;
          display: block;
          background: rgb(255, 255, 255);
          background: -webkit-linear-gradient(
            bottom,
            rgb(255, 255, 255) 0%,
            rgb(255, 255, 255) 30%,
            rgba(255, 255, 255, 0) 100%
          );
          background: linear-gradient(
            0deg,
            rgb(255, 255, 255) 0%,
            rgb(255, 255, 255) 30%,
            rgba(255, 255, 255, 0) 100%
          );
        }
      }
      @media (max-width: 768px) {
        max-height: 800px;
        overflow: hidden;
        position: relative;
      }
      &.-blurNone {
        &:before {
          display: none;
        }
      }
    }
    .salon-information-more-text {
      display: none;
      @media (max-width: 768px) {
        display: block;
        font-size: 13px;
        font-weight: bold;
        color: #26bfdb;
        text-align: center;
      }
    }
    .salon-information-close-text {
      display: none;
      @media (max-width: 768px) {
        font-size: 13px;
        font-weight: bold;
        color: #26bfdb;
        text-align: center;
      }
    }
    .salon-information-more-box-wrap {
      .posts {
        & > li {
          padding: 24px 0;
          @media (max-width: 768px) {
            &:first-of-type {
              padding-top: 0;
            }
          }
          h2 {
            line-height: 1.5;
          }
        }
        .text {
          font-weight: normal;
        }
      }
    }
  }
  .salon-plan {
    .salon-plan-title {
      font-family: 'Montserrat';
      font-weight: 300;
      font-size: 16px;
    }
    .salon-plan-bold {
      font-family: 'Montserrat';
      font-weight: 700;
      margin-right: 6px;
    }
    .salon-plan-content {
      position: relative;
    }
    .salon-plan-content {
      & + .salon-plan-content {
        margin-top: 80px;
      }
      &.is-secretPlan {
        .salon-plan-content-inner {
          border: 2px solid #000;
        }
        & + .salon-plan-content {
          margin-top: 50px;
        }
      }
      &.is-joined {
        .salon-plan-content-inner {
          border: 2px solid #26bfdb;
        }
        &.is-secretPlan {
          .salon-plan-content-inner {
            overflow: unset;
          }
        }
        & + .salon-plan-content {
          margin-top: 50px;
        }
      }
    }
    .salon-plan-content-inner {
      border: 2px solid #ffffff;
      box-shadow: 0 4px 24px rgba(107, 133, 141, 0.32);
      border-radius: 12px;
      margin-top: 18px;
      overflow: hidden;
      @media (max-width: 640px) {
        margin-top: 16px;
      }
      .button {
        height: 54px;
        border-radius: 27px;
        line-height: 54px;
      }
    }
    .salon-plan-image {
      padding-top: 53%;
      background-size: cover;
      background-position: center;
    }
    .salon-plan-content-title-box {
      padding: 16px;
      background-color: #ffffff;
    }
    .salon-plan-content-title {
      font-size: 20px;
      font-weight: bold;
      line-height: 1.5;
    }
    .salon-plan-content-title-period {
      font-size: 13px;
      font-weight: bold;
      position: relative;
      padding: 0 0 0 32px;
      margin-top: 10px;
      .salon-plan-icon {
        top: -20px;
      }
    }
    .salon-plan-content-small {
      font-size: 10px;
    }
    .salon-plan-text-box {
      border-top: 1px solid #dae3e3;
      padding: 16px;
      background-color: #ffffff;
      &.is-open {
        .salon-plan-more-text {
          display: none;
        }
        .salon-plan-close-text {
          display: block;
        }
        .salon-plan-more-box {
          max-height: 100%;
          &::before {
            display: none;
          }
        }
      }
    }
    .salon-plan-content-text {
      font-size: 13px;
      white-space: pre-wrap;
      font-weight: normal;
      & + .salon-plan-content-text {
        margin-top: 19px;
      }
    }
    .salon-plan-more-text {
      font-size: 13px;
      font-weight: bold;
      color: #26bfdb;
      text-align: center;
      -webkit-transition: 0.3s ease;
      transition: 0.3s ease;
      cursor: pointer;
      &:hover {
        opacity: 0.4;
      }
    }
    .salon-plan-close-text {
      display: none;
      font-size: 13px;
      font-weight: bold;
      color: #26bfdb;
      text-align: center;
      -webkit-transition: 0.3s ease;
      transition: 0.3s ease;
      cursor: pointer;
      margin-top: 24px;
      &:hover {
        opacity: 0.4;
      }
    }
    .salon-plan-more-box {
      max-height: 240px;
      overflow: hidden;
      position: relative;
      &::before {
        content: '';
        width: 100%;
        bottom: 0;
        position: absolute;
        height: 64px;
        display: block;
        background: rgb(255, 255, 255);
        background: linear-gradient(
          0deg,
          rgb(255, 255, 255) 0%,
          rgb(255, 255, 255) 30%,
          rgba(255, 255, 255, 0) 100%
        );
      }
      &.-under {
        &::before {
          content: '';
          display: none;
        }
      }
    }
    .salon-plan-bottom-box {
      background-color: #f7f7f7;
      text-align: center;
      padding: 0 8px 25px;
      border-radius: 0 0 12px 12px;
      &.is-no-button {
        padding-bottom: 20px;
      }
      &.-owner {
        padding-bottom: 16px;
      }
    }
    .salon-plan-price {
      color: #797979;
      font-size: 9px;
    }
    .salon-plan-price-large {
      font-family: 'Barlow Semi Condensed';
      font-weight: bold;
      font-size: 36px;
      color: #333;
      margin: 0 5px;
      &.is-border {
        position: relative;
        &::before {
          content: '';
          height: 4px;
          width: 100%;
          background: #f03b6b;
          position: absolute;
          top: 0;
          bottom: 0;
          margin: auto;
        }
      }
    }
    .salon-plan-price-bold {
      font-weight: bold;
      font-size: 13px;
      color: #333;
    }
    .salon-plan-status-list {
      display: -webkit-box;
      display: flex;
      -webkit-box-pack: center;
      justify-content: center;
      margin-bottom: 22px;
      &.-owner {
        margin-bottom: 0;
      }
      &.is-no-button {
        margin-bottom: 0;
      }
    }
    .salon-plan-status-item {
      font-size: 11px;
      padding: 0 12px 0 32px;
      position: relative;
      & + .salon-plan-status-item {
        border-left: 1px solid #dae3e3;
        padding: 0 12px 0 12px;
      }
    }
    .salon-plan-status-bold {
      font-weight: bold;
      font-size: 13px;
    }
    .salon-plan-icon {
      width: 24px;
      height: 24px;
      background-repeat: no-repeat;
      background-size: 100%;
      display: inline-block;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      margin: auto;
    }
    .button {
      position: absolute;
      bottom: -27px;
      left: 0;
      right: 0;
      width: 204px;
      margin: auto;
    }
    .salon-plan-tag {
      font-size: 13px;
      font-weight: bold;
      background-color: #ffff53;
      border-radius: 14px;
      line-height: 28px;
      padding: 0 10px;
      color: #333;
      position: relative;
      top: -4px;
    }
    .salon-plan-period {
      font-size: 14px;
      margin-top: -10px;
    }
    .salon-plan-image-info {
      background-color: #26bfdb;
      text-align: center;
      color: #ffffff;
      line-height: 32px;
      display: -webkit-box;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .salon-plan-check {
      width: 16px;
      height: 16px;
      margin-right: 4px;
      border: 1px solid #fff;
      border-radius: 4px;
    }
    .salon-plan-secretPlan-header {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #000;
      color: #fff;
      padding: 12px 0 13px;
      border-radius: 10px 10px 0 0;
      margin: -2px -2px 0;
    }
  }
`

const Recommend = styled.section`
  background-color: #f7f7f7;
  overflow: hidden;
  .recommend-inner {
    max-width: 1280px;
    padding: 92px 20px 72px;
    margin: auto;
    @media (max-width: 768px) {
      padding: 64px 15px 50px;
      background-color: #ffffff;
    }
  }
  .recommend-content {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      height: 1px;
      background-color: #d9d9d9;
      width: 100%;
      max-width: 1240px;
      margin-left: auto;
      margin-right: auto;
      left: 0;
      right: 0;
      top: -45px;
      @media (max-width: 768px) {
        top: -29px;
        margin-left: -15px;
        width: calc(100% + 30px);
      }
    }
  }
  .recommend-title {
    font-size: 24px;
    text-align: center;
    margin-top: 44px;
    position: relative;
    line-height: 1.4;
    font-weight: bold;
    @media (max-width: 768px) {
      margin-top: 28px;
    }
  }
  .recommend-title-text {
    display: inline-block;
    position: relative;
    margin: auto;
    &::before {
      content: '';
      width: 100%;
      height: 2px;
      background-color: #26bfdb;
      position: absolute;
      top: -44px;
      margin: auto;
      left: 0;
      right: 0;
      @media (max-width: 768px) {
        top: -28px;
      }
    }
  }
  .recommend-title-color {
    color: #26bfdb;
  }
  .recommend-list-wrap {
    position: relative;
    overflow: hidden;
  }
  .recommend-list-item-link {
    color: #333;
    transition: 0.3s ease;
    &:hover {
      .recommend-list-item-inner {
        opacity: 0.4;
      }
      .recommend-item-image {
        transform: scale(1.1);
      }
    }
  }
  .recommend-list {
    display: flex;
    flex-wrap: wrap;
    margin-top: 50px;
    position: relative;
    z-index: 2;
    margin-left: -15px;
    width: calc(100% + 30px);
    @media (max-width: 768px) {
      margin-top: 36px;
      margin-left: -7px;
      width: calc(100% + 14px);
    }
    &.is-item-5 {
      .recommend-list-item {
        @media (max-width: 1024px) {
          width: 20%;
        }
        @media (max-width: 768px) {
          width: 50%;
        }
      }
    }
  }
  .recommend-list-item {
    width: 20%;
    margin: 0 0 40px 0;
    @media (max-width: 1024px) {
      width: 25%;
    }
    @media (max-width: 768px) {
      width: 50%;
    }
  }
  .recommend-list-item-inner {
    padding: 0 15px;
    transition: 0.3s ease;
    @media (max-width: 768px) {
      padding: 0 6px;
    }
  }
  .recommend-item-image-wrap {
    overflow: hidden;
    position: relative;
  }
  .recommend-item-image {
    padding-top: 56%;
    background-size: cover;
    background-repeat: repeat;
    background-position: center;
    width: 100%;
    transition: 0.3s ease;
  }
  .recommend-item-title {
    font-size: 13px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-weight: bold;
    margin-top: 12px;
  }
  .recommend-user {
    display: -webkit-box;
    display: flex;
    margin-top: 14px;
  }
  .recommend-user-image {
    width: 20px;
    height: 20px;
    border-radius: 10px;
    background-size: cover;
    background-repeat: no-repeat;
    overflow: hidden;
  }
  .recommend-user-name {
    width: calc(100% - 30px);
    font-size: 11px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    margin-left: 8px;
  }
  .recommend-title-image {
    width: auto;
    max-width: 100%;
    height: 98px;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    margin: auto;
    @media (max-width: 768px) {
      top: -4;
      height: 60px;
    }
    &.is-keyword {
      top: 22px;
      @media (max-width: 768px) {
        top: 30px;
        z-index: 1;
      }
    }
  }
  .recommend-item-content {
    position: relative;
  }
  .recommend-free {
    background-color: #2bd6a7;
    color: #ffffff;
    height: 22px;
    border-radius: 11px;
    line-height: 22px;
    padding: 0 10px;
    font-weight: bold;
    font-size: 10px;
    position: absolute;
    top: -25px;
    left: 5px;
    display: inline-block;
    z-index: 2;
  }
`

const Reviews = styled.div`
  font-size: 16px;
  padding: 16px 0 20px;
  border-bottom: 1px solid #ddd;
  &:first-of-type {
    @media (max-width: 768px) {
      margin-top: 20px;
    }
  }
`
const ReviewsUserWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1;
  margin-bottom: 12px;
`
const ReviewsUsername = styled.p`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  font-weight: 500;
`
const ReviewsUsernameWithEntriedWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const ReviewsUserImage = styled.div`
  img {
    width: 42px;
    height: 42px;
    border-radius: 42px;
    overflow: hidden;
    object-fit: cover;
  }
`
const ReviewsUserEntried = styled.div`
  font-size: 12px;
  color: #666666;
`
const ReviewsText = styled.p`
  color: #333;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.5;
`

const ScratchBanner = styled.div`
  margin-bottom: 32px;
  @media (max-width: 640px) {
    margin-bottom: 48px;
  }
`

const ScratchBannerTitle = styled.h4`
  font-family: 'Montserrat';
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 12px;
`

const ScratchBannerImage = styled.img`
  width: 100%;
  height: auto;
  vertical-align: bottom;
  object-fit: cover;
  transition: 0.3s ease;
  aspect-ratio: 23 / 5;
  &:hover {
    opacity: 0.4;
  }
`
